/**
 * Typed API endpoints.
 *
 * Query parameter types come straight from the OpenAPI schema (snake_case,
 * as the API expects them); bodies and responses use the generated types.
 */

import { request, type QueryParams } from "./client";
import type { paths } from "./schema";
import type * as T from "./types.gen";

/** The query parameters accepted by `GET <path>`. */
type Query<P extends keyof paths> = paths[P] extends { get: { parameters: { query?: infer Q } } }
  ? NonNullable<Q>
  : never;

const q = (params: object | undefined) => params as QueryParams | undefined;

export const users = {
  me: () => request<T.UserResponse>("/users/me"),
  update: (body: T.UserUpdate) => request<T.UserResponse>("/users/me", { method: "PATCH", body }),
  bookmarks: (params?: Query<"/api/v1/users/me/bookmarks">) =>
    request<T.BookmarkListResponse>("/users/me/bookmarks", { params: q(params) }),
  addBookmark: (body: T.BookmarkCreate) =>
    request<T.BookmarkResponse>("/users/me/bookmarks", { method: "POST", body }),
  removeBookmark: (entityType: string, entityId: string) =>
    request<void>(
      `/users/me/bookmarks/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
      { method: "DELETE" },
    ),
};

export const seerah = {
  events: (params?: Query<"/api/v1/seerah/events">) =>
    request<T.SeerahEventListResponse>("/seerah/events", { params: q(params) }),
  event: (id: string) => request<T.SeerahEventDetail>(`/seerah/events/${id}`),
  filters: () => request<T.SeerahFiltersResponse>("/seerah/filters"),
};

export const worldHistory = {
  events: (params?: Query<"/api/v1/world-history/events">) =>
    request<T.WorldEventListResponse>("/world-history/events", { params: q(params) }),
  event: (id: string) => request<T.WorldEventDetail>(`/world-history/events/${id}`),
  filters: () => request<T.WorldHistoryFiltersResponse>("/world-history/filters"),
  timeline: (params?: Query<"/api/v1/world-history/timeline">) =>
    request<T.ParallelTimelineResponse>("/world-history/timeline", { params: q(params) }),
};

export const quran = {
  surahs: (params?: Query<"/api/v1/quran/surahs">) =>
    request<T.SurahListResponse>("/quran/surahs", { params: q(params) }),
  surah: (number: number) => request<T.SurahDetail>(`/quran/surahs/${number}`),
  ayah: (surah: number, ayah: number) => request<T.AyahDetail>(`/quran/ayahs/${surah}/${ayah}`),
  search: (params: Query<"/api/v1/quran/search">) =>
    request<T.AyahListResponse>("/quran/search", { params: q(params) }),
  themes: (params?: Query<"/api/v1/quran/themes">) =>
    request<T.ThemeListResponse>("/quran/themes", { params: q(params) }),
  themeAyahs: (themeId: string, params?: Query<"/api/v1/quran/themes/{theme_id}/ayahs">) =>
    request<T.AyahListResponse>(`/quran/themes/${themeId}/ayahs`, { params: q(params) }),
  filters: () => request<T.QuranFiltersResponse>("/quran/filters"),
};

export const isnad = {
  narrators: (params?: Query<"/api/v1/isnad/narrators">) =>
    request<T.NarratorListResponse>("/isnad/narrators", { params: q(params) }),
  narrator: (id: string) => request<T.NarratorDetail>(`/isnad/narrators/${id}`),
  hadiths: (params?: Query<"/api/v1/isnad/hadiths">) =>
    request<T.HadithListResponse>("/isnad/hadiths", { params: q(params) }),
  hadith: (id: string) => request<T.HadithDetail>(`/isnad/hadiths/${id}`),
  filters: () => request<T.IsnadFiltersResponse>("/isnad/filters"),
};

export const clarifier = {
  misconceptions: (params?: Query<"/api/v1/clarifier/misconceptions">) =>
    request<T.MisconceptionListResponse>("/clarifier/misconceptions", { params: q(params) }),
  misconception: (id: string) => request<T.MisconceptionDetail>(`/clarifier/misconceptions/${id}`),
  filters: () => request<T.ClarifierFiltersResponse>("/clarifier/filters"),
  feedback: (id: string, body: T.FeedbackCreate) =>
    request<T.FeedbackResponse>(`/clarifier/misconceptions/${id}/feedback`, { method: "POST", body }),
  versions: (slug: string) => request<T.MisconceptionVersionList>(`/clarifier/misconceptions/${slug}/versions`),
  version: (slug: string, version: number) =>
    request<T.MisconceptionDetail>(`/clarifier/misconceptions/${slug}/versions/${version}`),
  corrections: (params?: Query<"/api/v1/clarifier/corrections">) =>
    request<T.CorrectionLog>("/clarifier/corrections", { params: q(params) }),
  report: (id: string, body: T.CorrectionReportCreate) =>
    request<T.CorrectionReportStatus>(`/clarifier/misconceptions/${id}/correction-reports`, { method: "POST", body }),
  reportStatus: (reportId: string) => request<T.CorrectionReportStatus>(`/clarifier/correction-reports/${reportId}`),
};

/** Scholar review portal (scholars, imams and shaykhs; some calls are admin-only). */
export const review = {
  queue: () => request<T.ReviewQueue>("/admin/clarifier/review-queue"),
  list: (params?: Query<"/api/v1/admin/clarifier/misconceptions">) =>
    request<T.AdminMisconceptionList>("/admin/clarifier/misconceptions", { params: q(params) }),
  entry: (id: string) => request<T.AdminMisconceptionDetail>(`/admin/clarifier/misconceptions/${id}`),
  changeStatus: (id: string, body: T.StatusChange) =>
    request<T.AdminMisconceptionDetail>(`/admin/clarifier/misconceptions/${id}/status`, { method: "POST", body }),
  vote: (id: string, body: T.VoteCreate) =>
    request<T.AdminMisconceptionDetail>(`/admin/clarifier/misconceptions/${id}/votes`, { method: "POST", body }),
  verifySource: (sourceId: string, body: T.SourceVerification = {}) =>
    request<T.ClarificationSourceResponse>(`/admin/clarifier/sources/${sourceId}/verify`, { method: "POST", body }),
  gradeSource: (sourceId: string, body: T.ReviewerGradingCreate) =>
    request<T.ClarificationSourceResponse>(`/admin/clarifier/sources/${sourceId}/gradings`, { method: "POST", body }),
  suggest: (id: string, body: T.SuggestionCreate) =>
    request<T.SuggestionResponse>(`/admin/clarifier/misconceptions/${id}/suggestions`, { method: "POST", body }),
  suggestSource: (id: string, body: T.SourceSuggestionCreate) =>
    request<T.SuggestionResponse>(`/admin/clarifier/misconceptions/${id}/source-suggestions`, { method: "POST", body }),
  suggestRemoval: (id: string, body: T.SourceRemovalCreate) =>
    request<T.SuggestionResponse>(`/admin/clarifier/misconceptions/${id}/source-removals`, { method: "POST", body }),
  decide: (suggestionId: string, body: T.SuggestionDecision) =>
    request<T.SuggestionResponse>(`/admin/clarifier/suggestions/${suggestionId}/decision`, { method: "POST", body }),
  settings: () => request<T.ReviewSettingsSchema>("/admin/clarifier/review-settings"),
  updateSettings: (body: T.ReviewSettingsSchema) =>
    request<T.ReviewSettingsSchema>("/admin/clarifier/review-settings", { method: "PUT", body }),
  update: (id: string, body: T.MisconceptionUpdate) =>
    request<Record<string, unknown>>(`/admin/clarifier/misconceptions/${id}`, { method: "PATCH", body }),
  withdraw: (id: string, body: T.Withdrawal) =>
    request<T.AdminMisconceptionDetail>(`/admin/clarifier/misconceptions/${id}/withdraw`, { method: "POST", body }),
  reports: (params?: Query<"/api/v1/admin/clarifier/correction-reports">) =>
    request<T.CorrectionReportList>("/admin/clarifier/correction-reports", { params: q(params) }),
  updateReport: (reportId: string, body: T.CorrectionReportUpdate) =>
    request<T.CorrectionReportAdmin>(`/admin/clarifier/correction-reports/${reportId}`, { method: "PATCH", body }),
  profile: () => request<T.ScholarProfileResponse>("/users/me/scholar-profile"),
  updateProfile: (body: T.ScholarProfileUpdate) =>
    request<T.ScholarProfileResponse>("/users/me/scholar-profile", { method: "PUT", body }),
};

/** Admin: users, roles and scholar invitations. */
export const adminUsers = {
  list: (params?: Query<"/api/v1/admin/users">) => request<T.AdminUserList>("/admin/users", { params: q(params) }),
  setRole: (userId: string, body: T.UserRoleChange) =>
    request<T.AdminUserResponse>(`/admin/users/${userId}/role`, { method: "PUT", body }),
  invite: (body: T.ScholarInvite) => request<T.AdminUserResponse>("/admin/users/invite", { method: "POST", body }),
};

export const learning = {
  paths: (params?: Query<"/api/v1/learning/paths">) =>
    request<T.PathListResponse>("/learning/paths", { params: q(params) }),
  path: (id: string) => request<T.PathDetail>(`/learning/paths/${id}`),
  lesson: (id: string) => request<T.LessonDetail>(`/learning/lessons/${id}`),
  enroll: (pathId: string) =>
    request<T.EnrollResponse>(`/learning/paths/${pathId}/enroll`, { method: "POST" }),
  completeLesson: (lessonId: string) =>
    request<T.LessonCompleteResponse>(`/learning/lessons/${lessonId}/complete`, { method: "POST" }),
  progress: () => request<T.UserProgressResponse>("/learning/progress"),
};

export const scholar = {
  ask: (body: T.AskRequest) => request<T.AskResponse>("/scholar/ask", { method: "POST", body }),
  conversations: (params?: Query<"/api/v1/scholar/conversations">) =>
    request<T.ConversationListResponse>("/scholar/conversations", { params: q(params) }),
  conversation: (id: string) => request<T.ConversationDetail>(`/scholar/conversations/${id}`),
  deleteConversation: (id: string) =>
    request<unknown>(`/scholar/conversations/${id}`, { method: "DELETE" }),
};

export const analytics = {
  track: (body: T.TrackEventRequest) =>
    request<T.TrackEventResponse>("/analytics/track", { method: "POST", body }),
  popular: (params?: Query<"/api/v1/analytics/popular">) =>
    request<T.PopularContentResponse>("/analytics/popular", { params: q(params) }),
  dashboard: (params?: Query<"/api/v1/analytics/dashboard">) =>
    request<T.DashboardResponse>("/analytics/dashboard", { params: q(params) }),
};

export interface HealthResponse {
  status: "healthy" | "degraded";
  checks: Record<string, string>;
  version: string;
}

export const system = {
  health: () => request<HealthResponse>("/health"),
};
